import { SmallButton } from "@/components/atoms/Buttons/SmallButton";
import Droppable from "@/components/molecules/Droppable/Droppable";
import { Position } from "@/components/molecules/Player/SquadBuilderPlayer/Position/Position";
import { SquadBuilderPlayer } from "@/components/molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { thickArrow } from "@/designSystemIcons";
import { getPlayerRoleLabel } from "@/functions/getPlayerRoleLabel";
import { cn } from "@/lib/utils";
import { playersSelector } from "@/redux/players/playersSlice";
import { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { useDraggable } from "@dnd-kit/core";
import { useSelector } from "react-redux";

import { PitchItem } from "../PitchItem/PitchItem";
import { ClickPlayerType } from "../PitchLayout/PitchLayout";

type BenchProps = {
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  handleAddPlayer: (index: number) => void;
  handleRemovePlayer: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  handleOnClickPlayer: ClickPlayerType;
  handleOnClickPlayerMobile: (index: number) => void;
  changingPitchItemOnIndex: number;
  rightPanelMode: string;
  tabletOrLargerMode: boolean;
  isOverItem?: number;
  desktopMode: boolean;
};

const DraggableItem = ({
  id,
  children,
}: {
  id: number;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        "text-center touch-none scale-[90%] sb-v2-lg:scale-[85%] h-[130px] desktop:-mt-8 sb-v2-lg:-mt-12 -mx-4",
        isDragging && "opacity-50",
      )}
    >
      {children}
    </div>
  );
};

const BenchItem = ({
  item,
  benchIndex,
  isOverItem,
  handleAddPlayer,
  handleRemovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  handleChangePlayerRole,
  changingPitchItemOnIndex,
  rightPanelMode,
  tabletOrLargerMode,
  desktopMode,
}: {
  item: SquadBuilderPlayerType;
  benchIndex: number;
  isOverItem?: number;
  handleAddPlayer: (index: number) => void;
  handleRemovePlayer: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  handleOnClickPlayer: ClickPlayerType;
  handleOnClickPlayerMobile: (index: number) => void;
  changingPitchItemOnIndex: number;
  rightPanelMode: string;
  tabletOrLargerMode: boolean;
  desktopMode: boolean;
}) => {
  const { playerRoles } = useSelector(playersSelector);
  const isPlayer = item.resourceId;
  return (
    <div
      className="relative h-[130px] w-[70px] desktop:min-w-[112px] desktop:h-[195px] sb-v2-lg:h-[180px]  desktop:mb-6 sb-v2-lg:mb-4 flex justify-center items-center desktop:mt-auto"
      key={benchIndex}
    >
      <Droppable id={benchIndex}>
        <div className="touch-device:-mx-4">
          {isPlayer ? (
            <DraggableItem id={benchIndex}>
              <SquadBuilderPlayer
                player={item}
                mode={"default"}
                isOnPitch={!desktopMode}
                isOnBench={true}
                position={
                  item.possiblePositions.includes(item.position)
                    ? item.position
                    : item.preferredPosition
                }
                formationPosition={item.position}
                metaRating={item.metaRating}
                metaRatingZeroChem={item.metaRating}
                chemStyleId={0}
                playerRoleId={item.playerRoleId}
                tabletOrLargerMode={tabletOrLargerMode}
                desktopMode={desktopMode}
                handleRemovePlayer={() => handleRemovePlayer(benchIndex)}
                shouldScalePlayerRole={true}
                isSelected={changingPitchItemOnIndex === benchIndex}
                onClickPlayerRole={() => handleChangePlayerRole(benchIndex)}
                onClickPlayer={(e) => {
                  if (desktopMode) {
                    handleOnClickPlayer({
                      name: item.cardName,
                      resourceId: item.resourceId,
                      playerRoleId: item.playerRoleId,
                      chemistry: 0,
                      eaPlayerId: item.eaPlayerId,
                      evoPath: item.evolutionPath,
                      evoPathId: item.evoPathId,
                      chemstyleId: 250,
                    });
                  } else {
                    handleOnClickPlayerMobile(benchIndex);
                    e.stopPropagation();
                  }
                }}
              />
            </DraggableItem>
          ) : (
            <div
              className={cn(
                "w-full relative flex flex-col items-center",
                isOverItem === benchIndex ||
                  benchIndex === changingPitchItemOnIndex
                  ? "brightness-100"
                  : "brightness-75",
              )}
            >
              <PitchItem
                onClickPlayerPlaceHolder={() => handleAddPlayer(benchIndex)}
                onClickPlayerRole={() => handleChangePlayerRole(benchIndex)}
                activePlayerRole={
                  changingPitchItemOnIndex === benchIndex &&
                  rightPanelMode === "add-player-role"
                }
                activePlayerPlaceHolder={
                  changingPitchItemOnIndex === benchIndex &&
                  rightPanelMode === "add-player"
                }
                isOnBench={true}
                disablePlayerFilters={true}
                numberOfFilters={0}
                tabletOrLargerMode={tabletOrLargerMode}
              />
              <div className="absolute bottom-0 desktop:-bottom-4 z-10">
                <SmallButton
                  onClick={() => handleChangePlayerRole(benchIndex)}
                  disabled={!handleChangePlayerRole}
                  active={
                    changingPitchItemOnIndex === benchIndex &&
                    rightPanelMode === "add-player-role"
                  }
                >
                  {getPlayerRoleLabel(item.playerRoleId, playerRoles, true)}
                </SmallButton>
              </div>
              <div className="absolute -bottom-4 desktop:-bottom-10">
                <Position tabletOrLargerMode={tabletOrLargerMode}>
                  <div className="flex flex-row gap-x-1">
                    <span>{item.position}</span>
                  </div>
                </Position>
              </div>
            </div>
          )}
        </div>
      </Droppable>
    </div>
  );
};
const Bench = ({
  benchItems,
  tempBenchItems,
  handleAddPlayer,
  handleRemovePlayer,
  handleChangePlayerRole,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  changingPitchItemOnIndex,
  rightPanelMode,
  tabletOrLargerMode,
  desktopMode,
  isOverItem,
}: BenchProps) => {
  const substitutes =
    tempBenchItems.length > 0
      ? tempBenchItems.slice(0, 7)
      : benchItems.slice(0, 7);
  const reserves =
    tempBenchItems.length > 0 ? tempBenchItems.slice(7) : benchItems.slice(7);

  return (
    <>
      <button
        className="rotate-180 p-2 hidden desktop:block"
        onClick={() => {
          const scrollArea = document.querySelector(
            "[data-radix-scroll-area-viewport]",
          );
          if (scrollArea) {
            scrollArea.scrollTo({
              left: 0,
              behavior: "smooth",
            });
          }
        }}
      >
        {thickArrow}
      </button>
      <ScrollArea className="mb-6">
        <div className="flex items-center flex-col">
          <div className="flex flex-row gap-x-8">
            <div className="flex flex-col">
              <div
                className="w-full pl-2 text-xs py-0.5 rounded-t-md"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                }}
              >
                Subs
              </div>
              <div className="flex flex-row gap-x-4">
                {substitutes.map((item, index) => {
                  return (
                    <BenchItem
                      key={index}
                      item={item}
                      benchIndex={index + 11}
                      isOverItem={isOverItem}
                      handleAddPlayer={handleAddPlayer}
                      handleRemovePlayer={handleRemovePlayer}
                      handleChangePlayerRole={handleChangePlayerRole}
                      handleOnClickPlayer={handleOnClickPlayer}
                      handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                      changingPitchItemOnIndex={changingPitchItemOnIndex}
                      rightPanelMode={rightPanelMode}
                      tabletOrLargerMode={tabletOrLargerMode}
                      desktopMode={desktopMode}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col">
              <div
                className="w-full pl-2 text-xs py-0.5 rounded-t-md"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                }}
              >
                Reserves
              </div>
              <div className="flex flex-row gap-x-4">
                {reserves.map((item, index) => {
                  return (
                    <BenchItem
                      key={index}
                      item={item}
                      benchIndex={index + 18}
                      isOverItem={isOverItem}
                      handleAddPlayer={handleAddPlayer}
                      handleRemovePlayer={handleRemovePlayer}
                      handleChangePlayerRole={handleChangePlayerRole}
                      handleOnClickPlayer={handleOnClickPlayer}
                      handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                      changingPitchItemOnIndex={changingPitchItemOnIndex}
                      rightPanelMode={rightPanelMode}
                      tabletOrLargerMode={tabletOrLargerMode}
                      desktopMode={desktopMode}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <ScrollBar
          orientation="horizontal"
          className="overflow-y-hidden cursor-grab"
          forceMount
        />
      </ScrollArea>
      <button
        className="p-2 hidden desktop:block"
        onClick={() => {
          const scrollArea = document.querySelector(
            "[data-radix-scroll-area-viewport]",
          );
          if (scrollArea) {
            scrollArea.scrollTo({
              left: scrollArea.scrollWidth,
              behavior: "smooth",
            });
          }
        }}
      >
        {thickArrow}
      </button>
    </>
  );
};

export default Bench;
